import React, { useCallback } from 'react';
import {
  View,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { Hoverable } from './Hoverable';

export type TabSegmentVariant = 'pill' | 'underline' | 'chip';
export type TabSegmentSize = 'sm' | 'md';

export interface TabSegmentItem<TKey extends string = string> {
  key: TKey;
  label: string;
  count?: number;
}

export interface TabSegmentProps<TKey extends string = string> {
  items: TabSegmentItem<TKey>[];
  selectedKey: TKey;
  onChange: (key: TKey) => void;
  variant?: TabSegmentVariant;
  size?: TabSegmentSize;
  style?: StyleProp<ViewStyle>;
  fullWidth?: boolean;
}

interface TabButtonProps<TKey extends string> {
  item: TabSegmentItem<TKey>;
  isActive: boolean;
  variant: TabSegmentVariant;
  size: TabSegmentSize;
  onPress: (key: TKey) => void;
  colors: ReturnType<typeof useThemeColors>['colors'];
  fullWidth: boolean;
}

function TabButton<TKey extends string>({
  item,
  isActive,
  variant,
  size,
  onPress,
  colors,
  fullWidth,
}: TabButtonProps<TKey>) {
  const handlePress = useCallback(() => onPress(item.key), [onPress, item.key]);
  const heightStyle = size === 'sm' ? styles.tabSm : styles.tabMd;

  const containerByVariant: StyleProp<ViewStyle> = (() => {
    if (variant === 'underline') {
      return [
        styles.tabBase,
        styles.underlineTab,
        heightStyle,
        fullWidth && styles.underlineTabFullWidth,
        isActive && { borderBottomColor: colors.accent.blueBg },
      ];
    }
    if (variant === 'chip') {
      return [
        styles.tabBase,
        styles.chipTab,
        heightStyle,
        { borderColor: isActive ? colors.accent.blueBg : colors.card.border },
        isActive && { backgroundColor: colors.accent.blueBgLight },
      ];
    }
    // pill
    return [
      styles.tabBase,
      styles.pillTab,
      heightStyle,
      isActive && { backgroundColor: colors.accent.blueBg },
    ];
  })();

  const labelColor = (() => {
    if (variant === 'underline') {
      return isActive ? colors.text.primary : colors.text.dim;
    }
    if (variant === 'chip') {
      return isActive ? PRIMITIVE_COLORS.resioBlue : colors.text.dim;
    }
    return isActive ? PRIMITIVE_COLORS.resioBlue : colors.text.dim;
  })();

  return (
    <Hoverable
      hover={isActive ? 'none' : 'row'}
      style={containerByVariant}
      onPress={handlePress}
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
    >
      <Text style={[styles.label, size === 'sm' && styles.labelSm, { color: labelColor }]}>
        {item.label}
      </Text>
      {typeof item.count === 'number' ? (
        <View
          style={[
            styles.countBadge,
            {
              backgroundColor: isActive
                ? variant === 'underline'
                  ? colors.accent.blueBgLight
                  : colors.accent.blueBgStrong
                : colors.card.border,
            },
          ]}
        >
          <Text scale="micro" style={[styles.countText,
              { color: isActive ? PRIMITIVE_COLORS.resioBlue : colors.text.dimmer },
            ]}
          >
            {item.count}
          </Text>
        </View>
      ) : null}
    </Hoverable>
  );
}

function TabSegmentImpl<TKey extends string = string>({
  items,
  selectedKey,
  onChange,
  variant = 'pill',
  size = 'md',
  style,
  fullWidth = false,
}: TabSegmentProps<TKey>) {
  const { colors } = useThemeColors();

  const containerStyle: StyleProp<ViewStyle> = (() => {
    if (variant === 'underline') {
      return [styles.underlineContainer, { borderBottomColor: colors.separator }, style];
    }
    if (variant === 'chip') {
      return [styles.chipContainer, style];
    }
    return [
      styles.pillContainer,
      { backgroundColor: colors.card.bg, borderColor: colors.card.border },
      style,
    ];
  })();

  return (
    <View style={containerStyle}>
      {items.map((item) => (
        <TabButton<TKey>
          key={item.key}
          item={item}
          isActive={item.key === selectedKey}
          variant={variant}
          size={size}
          onPress={onChange}
          colors={colors}
          fullWidth={fullWidth}
        />
      ))}
    </View>
  );
}

export const TabSegment = React.memo(TabSegmentImpl) as typeof TabSegmentImpl;

const styles = StyleSheet.create({
  pillContainer: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 14,
    padding: 5,
    paddingBottom: 4,
  },
  underlineContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tabBase: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  pillTab: {
    flex: 1,
    borderRadius: 11,
  },
  underlineTab: {
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  underlineTabFullWidth: {
    flex: 1,
    paddingHorizontal: 0,
  },
  chipTab: {
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
  },
  tabSm: {
    minHeight: 32,
    paddingVertical: 6, // leaves room for the 18pt count badge inside a bordered chip
  },
  tabMd: {
    minHeight: 38,
    paddingVertical: 9,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  labelSm: {
    fontSize: 13,
  },
  countBadge: {
    minWidth: 18,
    minHeight: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  countText: {
    fontSize: 11,
    fontWeight: '700',
  },
});
